import { PageHeroMetric } from '@/components/ui/page-hero';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Info } from 'lucide-react';
import type { ReactNode } from 'react';

function InfoHintButton({ info, infoAriaLabel }: { info: string; infoAriaLabel: string }) {
  const isMobile = useIsMobile();

  const button = (
    <button
      type='button'
      className='text-fin-gray-400 hover:text-fin-gray-200 inline-flex size-11 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:outline-none'
      aria-label={infoAriaLabel}
    >
      <Info className='size-3.5' aria-hidden />
    </button>
  );

  if (isMobile) {
    return (
      <Popover>
        <PopoverTrigger asChild>{button}</PopoverTrigger>
        <PopoverContent side='top' className='max-w-xs text-sm'>
          {info}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side='top' className='max-w-xs'>
        {info}
      </TooltipContent>
    </Tooltip>
  );
}

interface PillarMetricWithInfoProps {
  value: ReactNode;
  info: string;
  infoAriaLabel: string;
  isLoading?: boolean;
  size?: 'primary' | 'secondary';
  trailing?: ReactNode;
  className?: string;
  /** Skip default metric typography when value brings its own styling (e.g. PnLIndicator). */
  unstyledValue?: boolean;
  /**
   * Opt-in: when set, the value rolls/counts up to this number on arrival instead
   * of popping in as static text. `formatValue` formats each animation frame and
   * must produce the same string as `value` at the target. Ignored while loading.
   */
  numericValue?: number;
  formatValue?: (n: number) => string;
  /** Extra classes on the rolling/static metric (e.g. P&L tone). */
  metricClassName?: string;
}

export function PillarMetricWithInfo({
  value,
  info,
  infoAriaLabel,
  isLoading = false,
  size = 'primary',
  trailing,
  className,
  unstyledValue = false,
  numericValue,
  formatValue,
  metricClassName,
}: PillarMetricWithInfoProps) {
  const valueClass =
    size === 'primary'
      ? 'text-fin-white text-2xl font-semibold tabular-nums sm:text-3xl'
      : 'text-fin-white text-base font-semibold tabular-nums';

  const canRoll = numericValue !== undefined && formatValue !== undefined;

  return (
    <span className={cn('inline-flex flex-wrap items-center gap-x-2 gap-y-1', className)}>
      {canRoll ? (
        <PageHeroMetric
          value={numericValue}
          format={formatValue}
          isLoading={isLoading}
          size={size}
          className={metricClassName}
        />
      ) : isLoading ? (
        <Skeleton className={size === 'primary' ? 'h-8 w-36' : 'h-5 w-28'} />
      ) : unstyledValue ? (
        value
      ) : (
        <span className={valueClass}>{value}</span>
      )}
      {trailing}
      <InfoHintButton info={info} infoAriaLabel={infoAriaLabel} />
    </span>
  );
}

interface PillarMetricSubtitleRowProps {
  label: string;
  value: ReactNode;
  info: string;
  infoAriaLabel: string;
  isLoading?: boolean;
  trailing?: ReactNode;
  unstyledValue?: boolean;
  numericValue?: number;
  formatValue?: (n: number) => string;
  metricClassName?: string;
}

/** Secondary hero metric row: muted label + value with info icon. */
export function PillarMetricSubtitleRow({
  label,
  value,
  info,
  infoAriaLabel,
  isLoading,
  trailing,
  unstyledValue,
  numericValue,
  formatValue,
  metricClassName,
}: PillarMetricSubtitleRowProps) {
  return (
    <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
      <span className='text-fin-gray-400 text-sm font-medium'>{label}</span>
      <PillarMetricWithInfo
        value={value}
        info={info}
        infoAriaLabel={infoAriaLabel}
        isLoading={isLoading}
        size='secondary'
        trailing={trailing}
        unstyledValue={unstyledValue}
        numericValue={numericValue}
        formatValue={formatValue}
        metricClassName={metricClassName}
      />
    </div>
  );
}
